/**
 * Keeping the decoded-image cache inside its cap.
 *
 * Cornerstone's image cache has an LRU (`decacheIfNecessaryUntilBytesAvailable`
 * in core's `cache.js`), but it skips every image carrying a `sharedCacheKey`,
 * and `isCacheable` does not count those images as reclaimable either. Core
 * means the key as "this image's pixels belong to a volume" — `_putVolumeCommon`
 * sets it to the volume id and `_decacheVolume` clears it. The wadouri loader
 * reuses the same field for something else: every image it resolves through
 * the fetch path (`loadImageFromPromise` in dicom-image-loader's
 * `wadouri/loadImage.js`) gets `sharedCacheKey` set to its object URL: every
 * frame requested while its object was still downloading, which is every
 * per-object single-frame instance and the leading window of every cine clip.
 * Only frames decoded from a dataset already in the cache (later cine frames,
 * bundle-seeded objects) go through `loadImageFromDataSet`, which sets none.
 *
 * So for this viewer the built-in LRU evicts next to nothing. Once the cap
 * fills, every further insert throws CACHE_SIZE_EXCEEDED: the cache stops
 * caching, cine and scrolling go back to the network frame by frame, and it
 * stays that way for the rest of the tab — a long echo session, or study after
 * study opened in place, is all it takes.
 *
 * Clearing the key would let the built-in LRU in, but it evicts one frame at a
 * time with no idea what is on screen, and evicting a fetch-path image runs its
 * `decache` hook, which unloads the object's wadouri dataset while the other
 * frames of the same clip are still cached. The wadouri metadata provider
 * answers from that dataset, so the next `setStack` of those frames fails
 * silently and the pane stays black (see `purgeDataSetCache` in ./datasetSeed
 * and ./firstFrameRecovery).
 *
 * This module is the eviction policy instead. Just before a newly loaded image
 * is filed (see `handleImageLoaded` for why that excludes cache hits), if the
 * insert would take the cache over its high-water mark, whole OBJECTS — every
 * cached frame of one DICOM instance, plus its dataset — are evicted least
 * recently used first until it is back under the low-water mark. An object is
 * never evicted while it is bound to a viewport, backs a cached volume (MPR),
 * backs a cached subtraction image, or has a frame still loading.
 */
import { cache, Enums, eventTarget, getRenderingEngines } from '@cornerstonejs/core';
import parseImageId from '@cornerstonejs/dicom-image-loader/wadouri/parseImageId';
import { releaseDataSet } from './datasetSeed';
import { parseSubtractionImageId } from './subtraction';
/**
 * Trim when an insert would take the cache past this fraction of its cap.
 *
 * Above the prefetcher's look-ahead ceiling (`lookaheadCacheCeiling`, 0.85, in
 * ./prefetchManager) on purpose: background look-ahead stops itself below this
 * line, so only loads the reader is waiting on ever trigger a trim, and
 * speculative warming can never evict anything.
 */
export const EVICTION_HIGH_WATER_FRACTION = 0.9;
/**
 * Trim down to this fraction of the cap. The gap to the high-water mark is
 * what keeps trimming occasional rather than one object per insert.
 */
export const EVICTION_LOW_WATER_FRACTION = 0.8;
/**
 * Core keeps its entries in a private map and offers no way to list them; the
 * public lookups (`getImageLoadObject`, `getImage`) need an id up front and
 * also refresh the entry's LRU timestamp. This reads the map without touching
 * it, and evictions go back through the public `removeImageLoadObject`.
 */
function cachedImageEntries() {
    return (cache._imageCache?.values() ??
        []);
}
const WADOURI_PREFIX = 'wadouri:';
/** The wadouri dataset key an imageId reads from, or null for other schemes. */
function objectKey(imageId) {
    return imageId.startsWith(WADOURI_PREFIX) ? parseImageId(imageId).url : null;
}
/**
 * The wadouri objects `imageId` needs to render: itself, or for a subtraction
 * image the two slices it is computed from and takes its metadata from.
 */
function addSourceObjectKeys(imageId, into) {
    const subtraction = parseSubtractionImageId(imageId);
    if (subtraction) {
        addSourceObjectKeys(subtraction.postImageId, into);
        addSourceObjectKeys(subtraction.preImageId, into);
        return;
    }
    const key = objectKey(imageId);
    if (key)
        into.add(key);
}
/** Objects bound to a viewport (on screen) or to a cached volume (MPR/3D). */
function boundObjectKeys() {
    const keys = new Set();
    for (const engine of getRenderingEngines() ?? []) {
        let viewports;
        try {
            viewports = engine.getViewports();
        }
        catch {
            // A destroyed engine has nothing on screen.
            continue;
        }
        for (const viewport of viewports) {
            let imageIds;
            try {
                imageIds = viewport.getImageIds?.();
            }
            catch {
                // A volume viewport with no volume yet throws here; its images are
                // covered by the volume pass below once the volume exists.
                continue;
            }
            for (const imageId of imageIds ?? [])
                addSourceObjectKeys(imageId, keys);
        }
    }
    for (const volume of cache.getVolumes()) {
        for (const imageId of volume?.imageIds ?? [])
            addSourceObjectKeys(imageId, keys);
    }
    return keys;
}
/**
 * Bring the cache back under the low-water mark if `incomingBytes` more would
 * take it over the high-water mark. Returns the bytes freed.
 */
export function trimImageCache(incomingBytes = 0) {
    const maxBytes = cache.getMaxCacheSize();
    const startBytes = cache.getCacheSize();
    if (startBytes + incomingBytes <= maxBytes * EVICTION_HIGH_WATER_FRACTION)
        return 0;
    const targetBytes = maxBytes * EVICTION_LOW_WATER_FRACTION;
    const bound = boundObjectKeys();
    const objects = new Map();
    for (const entry of cachedImageEntries()) {
        const key = objectKey(entry.imageId);
        if (key === null) {
            // A cached subtraction image takes its metadata from its sources, so
            // they stay for as long as it does.
            if (parseSubtractionImageId(entry.imageId))
                addSourceObjectKeys(entry.imageId, bound);
            continue;
        }
        let object = objects.get(key);
        if (!object) {
            object = { imageIds: [], lastUsed: 0, pinned: false };
            objects.set(key, object);
        }
        object.imageIds.push(entry.imageId);
        object.lastUsed = Math.max(object.lastUsed, entry.timeStamp);
        // A frame still loading lands after we would have released the dataset it
        // was decoded from. A key that is not the object's own URL is a volume's.
        if (!entry.loaded || (entry.sharedCacheKey && entry.sharedCacheKey !== key)) {
            object.pinned = true;
        }
    }
    const evictable = [...objects]
        .filter(([key, object]) => !object.pinned && !bound.has(key))
        .sort(([, a], [, b]) => a.lastUsed - b.lastUsed);
    for (const [key, object] of evictable) {
        if (cache.getCacheSize() <= targetBytes)
            break;
        for (const imageId of object.imageIds) {
            // force: the wadouri pin is exactly what this module overrides.
            cache.removeImageLoadObject(imageId, { force: true });
        }
        releaseDataSet(key);
    }
    return startBytes - cache.getCacheSize();
}
/**
 * IMAGE_LOADED is the pre-insert seam only for a cache MISS: `imageLoader.js`
 * attaches it to a fresh load promise before `putImageLoadObject` does, so it
 * runs while the entry is still unfiled. On a HIT the loader re-fires it on
 * the cached promise — for every displayed frame, every cine tick and scroll
 * step — and that image is already counted in the cache size. Those are
 * skipped: there is nothing to make room for, and a trim that cannot free
 * anything (the cache held above the mark by what is on screen) would
 * otherwise rescan the whole cache on the render path. `isLoaded` reads the
 * entry without refreshing its LRU timestamp, unlike `getImageLoadObject`.
 */
function handleImageLoaded(event) {
    const image = event
        .detail?.image;
    if (image?.imageId && cache.isLoaded(image.imageId))
        return;
    trimImageCache(image?.sizeInBytes ?? 0);
}
let subscribed = false;
/**
 * Evict ahead of every insert that needs the room. Idempotent, like the other
 * init-time subscriptions.
 */
export function subscribeToImageCacheEviction() {
    if (subscribed)
        return;
    subscribed = true;
    eventTarget.addEventListener(Enums.Events.IMAGE_LOADED, handleImageLoaded);
}
